import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Trash2, MessageCircle, ShoppingBag, Copy, ArrowLeft, ArrowRight, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { STORE } from "@/lib/config";
import { toast } from "sonner";

export default function CartDrawer() {
  const { open, setOpen, items, updateQty, remove, total, count, clear } = useCart();
  const [step, setStep] = useState("cart"); // "cart" | "checkout"
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");

  // Reset to cart view when drawer closes
  useEffect(() => {
    if (!open) setTimeout(() => setStep("cart"), 250);
  }, [open]);

  // If cart empties while in checkout, go back
  useEffect(() => {
    if (items.length === 0 && step === "checkout") setStep("cart");
  }, [items.length, step]);

  const buildOrderText = () => {
    const lines = [
      "Pozdrav MOBY SHOP! Želim naručiti:",
      "",
      ...items.map((i, idx) => `${idx + 1}. ${i.name} × ${i.qty} = ${(i.price * i.qty).toFixed(2)} KM`),
      "",
      `Ukupno: ${total.toFixed(2)} KM`,
      "",
      "Moji podaci:",
      `Ime i prezime: ${name || "___"}`,
      `Adresa za dostavu: ${address || "___"}`,
      `Broj telefona: ${phone || "___"}`,
      note ? `Napomena: ${note}` : "",
      "",
      "Hvala!",
    ].filter(Boolean);
    return lines.join("\n");
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    }
  };

  const validateBuyer = () => {
    if (!name.trim()) return "Molimo unesite ime i prezime";
    if (!address.trim()) return "Molimo unesite adresu za dostavu";
    if (!phone.trim()) return "Molimo unesite broj telefona";
    return null;
  };

  const handleMessengerOrder = async () => {
    const err = validateBuyer();
    if (err) { toast.error(err); return; }
    await copyToClipboard(buildOrderText());
    window.open(`https://m.me/${STORE.messengerUsername}`, "_blank", "noopener,noreferrer");
    toast.success("Poruka je kopirana! U Messengeru samo zalijepi (Ctrl+V ili dugi pritisak).", {
      duration: 6000,
    });
  };

  const handleWhatsAppOrder = () => {
    const err = validateBuyer();
    if (err) { toast.error(err); return; }
    const text = encodeURIComponent(buildOrderText());
    const wa = STORE.mobile.replace(/\D/g, "");
    window.open(`https://wa.me/${wa}?text=${text}`, "_blank", "noopener,noreferrer");
    toast.success("Otvaramo WhatsApp sa pripremljenom porukom!");
  };

  const handleCopyOnly = async () => {
    const err = validateBuyer();
    if (err) { toast.error(err); return; }
    await copyToClipboard(buildOrderText());
    toast.success("Narudžba kopirana — sad je zalijepi gdje želiš!");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md flex flex-col p-0 bg-white"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-neutral-200">
          <SheetTitle className="flex items-center gap-3 text-2xl font-bold">
            {step === "checkout" ? (
              <>
                <button
                  onClick={() => setStep("cart")}
                  data-testid="checkout-back-btn"
                  className="w-9 h-9 grid place-items-center rounded-full hover:bg-neutral-100 text-neutral-600"
                  aria-label="Nazad"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <User className="w-6 h-6 text-[#D32F2F]" />
                Vaši podaci
              </>
            ) : (
              <>
                <ShoppingBag className="w-6 h-6 text-[#D32F2F]" />
                Košarica {count > 0 && <span className="text-[#D32F2F]">({count})</span>}
              </>
            )}
          </SheetTitle>

          {/* Step indicator */}
          {items.length > 0 && (
            <div className="flex items-center gap-2 pt-3">
              <StepDot active={step === "cart"} done={step === "checkout"} label="Košarica" />
              <div className="flex-1 h-px bg-neutral-200" />
              <StepDot active={step === "checkout"} label="Podaci" />
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {step === "cart" ? (
              <motion.div
                key="cart-view"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.25 }}
                className="px-6 py-4"
              >
                {items.length === 0 ? (
                  <div className="py-20 text-center" data-testid="cart-empty">
                    <ShoppingBag className="w-16 h-16 mx-auto text-neutral-300 mb-4" />
                    <h3 className="font-bold text-neutral-900 mb-1">Košarica je prazna</h3>
                    <p className="text-sm text-neutral-500">
                      Dodajte proizvode da napravite narudžbu.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        data-testid={`cart-item-${item.id}`}
                        className="flex gap-4 p-3 rounded-xl border border-neutral-200"
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-20 h-20 rounded-lg object-cover bg-neutral-100"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-neutral-900 line-clamp-1">{item.name}</div>
                          <div className="text-sm text-[#D32F2F] font-semibold mt-0.5">
                            {item.price.toFixed(2)} KM
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-1 border border-neutral-200 rounded-full">
                              <button
                                data-testid={`qty-minus-${item.id}`}
                                onClick={() => updateQty(item.id, item.qty - 1)}
                                className="w-7 h-7 grid place-items-center hover:bg-neutral-100 rounded-full"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="px-2 text-sm font-medium min-w-[24px] text-center">
                                {item.qty}
                              </span>
                              <button
                                data-testid={`qty-plus-${item.id}`}
                                onClick={() => updateQty(item.id, item.qty + 1)}
                                className="w-7 h-7 grid place-items-center hover:bg-neutral-100 rounded-full"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <button
                              data-testid={`remove-${item.id}`}
                              onClick={() => remove(item.id)}
                              className="text-neutral-400 hover:text-[#D32F2F] p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="checkout-view"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.25 }}
                className="px-6 py-4 space-y-4"
              >
                {/* Order summary */}
                <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-[#D32F2F] font-bold mb-2">
                    Sažetak narudžbe
                  </div>
                  <div className="space-y-1 text-sm">
                    {items.map((i) => (
                      <div key={i.id} className="flex justify-between text-neutral-700">
                        <span className="truncate pr-2">
                          {i.name} <span className="text-neutral-400">× {i.qty}</span>
                        </span>
                        <span className="font-medium whitespace-nowrap">
                          {(i.price * i.qty).toFixed(2)} KM
                        </span>
                      </div>
                    ))}
                    <div className="h-px bg-neutral-200 my-2" />
                    <div className="flex justify-between font-bold text-neutral-900">
                      <span>Ukupno</span>
                      <span className="text-[#D32F2F]" data-testid="cart-total">
                        {total.toFixed(2)} KM
                      </span>
                    </div>
                  </div>
                </div>

                {/* Buyer info form */}
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="ci-name" className="text-xs">Ime i prezime *</Label>
                    <Input
                      id="ci-name"
                      data-testid="buyer-name"
                      placeholder="npr. Marko Marković"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ci-addr" className="text-xs">Adresa za dostavu *</Label>
                    <Input
                      id="ci-addr"
                      data-testid="buyer-address"
                      placeholder="Ulica i broj, mjesto, poštanski broj"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ci-phone" className="text-xs">Broj telefona *</Label>
                    <Input
                      id="ci-phone"
                      data-testid="buyer-phone"
                      type="tel"
                      placeholder="+387 6X XXX XXX"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ci-note" className="text-xs">Napomena (opcionalno)</Label>
                    <Textarea
                      id="ci-note"
                      data-testid="buyer-note"
                      placeholder="npr. dostava poslije 16h, boja, veličina…"
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer actions */}
        {items.length > 0 && (
          <div className="border-t border-neutral-200 px-6 py-4 bg-neutral-50 space-y-3">
            {step === "cart" ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-neutral-600">Ukupno</span>
                  <span
                    className="text-2xl font-extrabold text-[#D32F2F]"
                    style={{ fontFamily: "Cabinet Grotesk" }}
                    data-testid="cart-total-step1"
                  >
                    {total.toFixed(2)} KM
                  </span>
                </div>
                <Button
                  data-testid="cart-checkout-btn"
                  onClick={() => setStep("checkout")}
                  className="w-full bg-[#D32F2F] hover:bg-[#B71C1C] text-white rounded-full h-12 font-semibold text-base"
                >
                  Naruči
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <button
                  onClick={clear}
                  data-testid="cart-clear-btn"
                  className="w-full text-xs text-neutral-400 hover:text-[#D32F2F] py-1"
                >
                  Isprazni košaricu
                </button>
              </>
            ) : (
              <>
                <Button
                  data-testid="cart-messenger-btn"
                  onClick={handleMessengerOrder}
                  className="w-full bg-[#0084FF] hover:bg-[#0066CC] text-white rounded-full h-12 font-semibold text-base"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Pošalji putem Messengera
                </Button>
                <Button
                  data-testid="cart-whatsapp-btn"
                  onClick={handleWhatsAppOrder}
                  className="w-full bg-[#25D366] hover:bg-[#1FAD53] text-white rounded-full h-11 font-semibold"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Pošalji putem WhatsAppa
                </Button>
                <button
                  onClick={handleCopyOnly}
                  data-testid="cart-copy-btn"
                  className="w-full flex items-center justify-center gap-2 text-sm text-neutral-600 hover:text-[#D32F2F] py-2"
                >
                  <Copy className="w-4 h-4" /> Samo kopiraj narudžbu
                </button>
                <p className="text-xs text-neutral-500 text-center leading-relaxed pt-1">
                  <strong>Messenger</strong>: poruka se kopira — samo zalijepi (Ctrl+V / dugi pritisak).
                  <br />
                  <strong>WhatsApp</strong>: automatski popuni poruku sa svim podacima.
                </p>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StepDot({ active, done, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={`w-2 h-2 rounded-full transition-colors ${
          active ? "bg-[#D32F2F] ring-4 ring-red-100" : done ? "bg-[#D32F2F]" : "bg-neutral-300"
        }`}
      />
      <span
        className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${
          active ? "text-[#D32F2F]" : done ? "text-neutral-600" : "text-neutral-400"
        }`}
      >
        {label}
      </span>
    </div>
  );
}
